import type { User } from 'app/types/user';
import { useAtomValue, useAtom, useSetAtom } from 'jotai/index';
import { presentInsuredDetailsAtom, userInfoAtom } from 'app/atoms';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { Trans, useTranslation } from 'react-i18next';
import { InsuredBadge } from 'app/design/icons/insured-badge';

import { Pressable } from 'react-native';
import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import type { StyleProps } from 'react-native-reanimated';
import { ChatBubble } from 'app/design/icons/chat-bubble';

import type { Profile } from 'app/types/profile';
import { useMyProfile } from 'app/network/profile';
import { Button, PressableType } from 'app/design/primitives/button';
import { StorageKey, useStoredValue } from 'app/lib/storage';
import { safelyOpenURL } from 'app/lib/navigation';
import { preparePolicyUrl } from 'app/lib/text';
import { useUser } from 'app/network/user';
import { useNavigation } from 'app/hooks/navigation';
import { SUPPORT_CHANNEL_URL } from 'app/network/chat/channel';
import { navigateToChat } from 'app/screens/chat/navigator-helper';
import { getI18nLanguage } from 'app/lib/language';
import { useUISize } from 'app/utils/web-helpers';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconChevronRight, IconCircleCheck, IconClock, IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

export const useInsuredUsers = (
  otherUser: Pick<User, 'insured'> | undefined,
  placement: 'explore_needs' | 'listing' | 'profile' | 'chat' | 'help' | 'my-plan' | 'landing-page',
) => {
  const userInfo = useAtomValue(userInfoAtom);

  if (!userInfo?.insured && !otherUser?.insured) {
    return null;
  }

  if (placement === 'listing' || placement === 'explore_needs' || placement === 'chat') {
    return {
      title: 'listing.title',
      text: userInfo?.insured ? 'listing.text.own' : 'listing.text.other',
      link: 'link.more_details',
    };
  }

  if (placement === 'landing-page') {
    return {
      title: 'landing-page.title',
      text: userInfo?.insured ? 'landing-page.text' : null,
      link: 'link.more_details',
    };
  }

  if (placement === 'my-plan') {
    return {
      title: 'myPlan.title',
      text: 'myPlan.text',
      link: 'link.more_details',
    };
  }

  if (placement === 'profile') {
    return {
      title: 'profile.title',
      link: 'link.more_details',
    };
  }

  if (placement === 'help') {
    return {
      title: 'listing.text.own',
      link: 'link.more_details',
      button: 'help.button',
    };
  }
};

export const useInsuredUserOptional = () => {
  const userInfo = useAtomValue(userInfoAtom);
  const [isOptionalComponentVisible] = useStoredValue<boolean>(StorageKey.InsuredOptionalComponentsVisible, true);
  return isOptionalComponentVisible && userInfo?.insured;
};

export function useHideInsuredUser({
  otherUser,
  placement,
}: {
  otherUser: (Pick<User, 'insured'> & { profile?: Pick<Profile, 'firstName'> }) | undefined;
  placement: 'explore_needs' | 'listing' | 'profile' | 'chat' | 'help' | 'my-plan' | 'landing-page';
}) {
  const texts = useInsuredUsers(otherUser, placement);

  const isOptional = placement === 'explore_needs' || placement === 'chat';

  const [isOptionalComponentVisible, setIsOptionalComponentVisible] = useStoredValue<boolean>(
    StorageKey.InsuredOptionalComponentsVisible,
    true,
  );
  const hideInsuredUser = !texts || (isOptional && !isOptionalComponentVisible);
  return { hideInsuredUser, setIsOptionalComponentVisible, isOptional, texts };
}

type InsuredPlacement = 'explore_needs' | 'listing' | 'profile' | 'chat' | 'help' | 'my-plan' | 'landing-page';

function getInsuredUserAppearance({
  isInline,
  isGoodToKnow,
  isLargeUI,
  placement,
  showText,
}: {
  isInline: boolean;
  isGoodToKnow: boolean;
  isLargeUI: boolean;
  placement: InsuredPlacement;
  showText: boolean;
}) {
  const fillsCard = isLargeUI && !isInline && !isGoodToKnow;
  const backgroundColor = isInline
    ? 'transparent'
    : isGoodToKnow
      ? '#EDF8F5'
      : placement === 'chat'
        ? theme.colors['surface.secondary-neutral']
        : theme.colors['surface.secondary'];
  const padding = isInline
    ? 0
    : isGoodToKnow
      ? theme.spacing['spacing.general.x6']
      : !showText
        ? isLargeUI
          ? theme.spacing['spacing.general.x6']
          : theme.spacing['spacing.general.x1']
        : theme.spacing['spacing.general.x3'];
  const badgeColor = isInline
    ? theme.colors['surface.secondary']
    : isGoodToKnow
      ? '#B8ECE0'
      : placement === 'chat'
        ? theme.colors['surface.tertiary']
        : 'white';

  return {
    backgroundColor,
    badgeColor,
    borderRadius: isInline ? 0 : isGoodToKnow ? 16 : theme.borderRadii['radius.lg'],
    columnFlex: fillsCard ? 1 : undefined,
    columnGap: isInline || isGoodToKnow ? ('spacing.general.x2' as const) : ('spacing.general.x6' as const),
    columnJustifyContent: fillsCard ? ('center' as const) : undefined,
    padding,
    pressableStyle: [fillsCard && { flex: 1 }, !isInline && { alignSelf: 'stretch' as const, width: '100%' as const }],
    rowGap: isInline || isGoodToKnow ? ('spacing.general.x2' as const) : ('spacing.general.x4' as const),
  };
}

function InsuredUserCopy({
  isInline,
  isOptional,
  showText,
  text,
  title,
}: Readonly<{
  isInline: boolean;
  isOptional: boolean;
  showText: boolean;
  text?: string;
  title: React.ReactNode;
}>) {
  if (showText) {
    return (
      <>
        <Text fontWeight="600" style={{ marginRight: isOptional ? 24 : 0 }} variant="bodyBase">
          {title}
        </Text>
        {text ? <Text variant="bodyBase">{text}</Text> : null}
      </>
    );
  }
  if (!text) return null;
  return (
    <Text
      color={isInline ? 'text.secondary' : 'text.primary'}
      fontWeight={isInline ? '600' : '700'}
      style={{ marginRight: isOptional ? 24 : 0 }}
      variant={isInline ? 'bodySmall' : 'bodyBase'}
    >
      {title}
    </Text>
  );
}

function InsuredUserChevron({ isInline, isOptional }: Readonly<{ isInline: boolean; isOptional: boolean }>) {
  if (isOptional) return null;
  return (
    <IconChevronRight
      strokeWidth={ICON_STROKE_WIDTH}
      size={isInline ? 18 : 28}
      color={isInline ? theme.colors['icon.tertiary'] : theme.colors['text.primary']}
    />
  );
}

interface InsuredUserCardProps {
  buttonText?: string;
  isLargeUI: boolean;
  isOptional: boolean;
  onClose: () => void;
  onPress: () => void;
  onSupportPress: () => void;
  placement: InsuredPlacement;
  showText: boolean;
  style?: StyleProps['Row'];
  text?: string;
  title: React.ReactNode;
  variant: 'card' | 'inline' | 'good-to-know';
}

function InsuredUserCard({
  buttonText,
  isLargeUI,
  isOptional,
  onClose,
  onPress,
  onSupportPress,
  placement,
  showText,
  style,
  text,
  title,
  variant,
}: Readonly<InsuredUserCardProps>) {
  const isInline = variant === 'inline';
  const isGoodToKnow = variant === 'good-to-know';
  const Content = isGoodToKnow ? Column : Row;
  const { t } = useTranslation('insured');
  const appearance = getInsuredUserAppearance({ isInline, isGoodToKnow, isLargeUI, placement, showText });

  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      style={[appearance.pressableStyle, isGoodToKnow ? style : undefined]}
    >
      <Column
        flex={appearance.columnFlex}
        gap={appearance.columnGap}
        justifyContent={appearance.columnJustifyContent}
        style={{
          backgroundColor: appearance.backgroundColor,
          padding: appearance.padding,
          borderRadius: appearance.borderRadius,
          width: '100%',
          alignSelf: 'stretch',
          ...(!isGoodToKnow ? style : undefined),
        }}
      >
        <Content
          gap={appearance.rowGap}
          alignItems={isGoodToKnow ? 'flex-start' : 'center'}
          width={isGoodToKnow ? '100%' : undefined}
        >
          <InsuredBadge
            color={appearance.badgeColor}
            height={isGoodToKnow ? 32 : undefined}
            width={isGoodToKnow ? 32 : undefined}
          />
          <Column flex={isGoodToKnow ? undefined : 1} width="100%" justifyContent="center">
            <InsuredUserCopy
              isInline={isInline}
              isOptional={isOptional}
              showText={showText}
              text={text}
              title={title}
            />
          </Column>
          {!isGoodToKnow ? <InsuredUserChevron isInline={isInline} isOptional={isOptional} /> : null}
        </Content>
        {isGoodToKnow ? (
          <Row alignItems="center" gap="spacing.general.x2">
            <Text color="text.brand" fontSize={14} fontWeight="600">
              {t('link.more_details')}
            </Text>
            <IconChevronRight color={theme.colors['text.brand']} size={16} strokeWidth={ICON_STROKE_WIDTH} />
          </Row>
        ) : null}
        {buttonText ? (
          <Button onPress={onSupportPress} style={{ height: 48, borderRadius: 24 }} variant="primary">
            {buttonText}
          </Button>
        ) : null}
      </Column>
      {isOptional ? (
        <Column style={{ position: 'absolute', top: 10, right: 10 }}>
          <Pressable onPress={onClose}>
            <IconX strokeWidth={ICON_STROKE_WIDTH} size={24} />
          </Pressable>
        </Column>
      ) : null}
    </Pressable>
  );
}

export function InsuredUser({
  otherUser,
  placement,
  style,
  showText = true,
  variant = 'card',
}: {
  otherUser: (Pick<User, 'insured'> & { profile?: Pick<Profile, 'firstName'> }) | undefined;
  placement: 'explore_needs' | 'listing' | 'profile' | 'chat' | 'help' | 'my-plan' | 'landing-page';
  style?: StyleProps['Row'];
  showText?: boolean;
  variant?: 'card' | 'inline' | 'good-to-know';
}) {
  const { t } = useTranslation('insured');
  const navigation = useNavigation();
  const { isLargeUI } = useUISize();

  const { data: myProfile } = useMyProfile();
  const userInfo = useAtomValue(userInfoAtom);
  const setInsuredDetailsOtherUser = useSetAtom(presentInsuredDetailsAtom);
  const { hideInsuredUser, setIsOptionalComponentVisible, isOptional, texts } = useHideInsuredUser({
    otherUser,
    placement,
  });

  if (hideInsuredUser || !texts) {
    return null;
  }
  const showDetails = () => {
    setInsuredDetailsOtherUser(userInfo?.insured ? userInfo : otherUser!);
  };

  const goToSupportChat = () => {
    navigateToChat(navigation, SUPPORT_CHANNEL_URL, {
      i18nKey: `contactSupportAboutIncidentPrompt.insured`,
      ns: 'one-loan',
      values: { name: myProfile?.firstName ?? '' },
    });
  };

  const onClose = () => {
    setIsOptionalComponentVisible(false);
  };
  const displayName = otherUser?.profile?.firstName ?? '';
  const translatedText = texts.text ? t(texts.text, { name: displayName }) : undefined;
  const buttonText = texts.button ? t(texts.button) : undefined;

  return (
    <InsuredUserCard
      buttonText={buttonText}
      isLargeUI={isLargeUI}
      isOptional={isOptional}
      onClose={onClose}
      onPress={showDetails}
      onSupportPress={goToSupportChat}
      placement={placement}
      showText={showText}
      style={style}
      text={translatedText}
      title={
        variant === 'good-to-know' || placement === 'profile' ? (
          <Trans
            ns="insured"
            i18nKey={texts.title}
            values={{ name: displayName }}
            components={{ strong: <Text fontSize={14} lineHeight={17} fontWeight="700" /> }}
          />
        ) : (
          t(texts.title, { name: displayName })
        )
      }
      variant={variant}
    />
  );
}

interface StepProps {
  text: string;
  Icon: React.FC<{ color: string; fillColor: string; width: number; height: number }>;
  size: number;
}

export function InsuredUserDetails() {
  const [details, setDetails] = useAtom(presentInsuredDetailsAtom);

  return details ? <InsuredUserDetailsSheet details={details} onClose={() => setDetails(false)} /> : null;
}

/** Mount inside the presenting screen when it is itself a native modal. */
export function InsuredUserDetailsSheet({
  details,
  onClose,
}: {
  details: Pick<User, 'insured'> & { profile?: Pick<Profile, 'firstName'> };
  onClose: () => void;
}) {
  const { t } = useTranslation('insured');
  const i18nLanguage = getI18nLanguage();
  const { data: user } = useUser();
  const { isLargeUI } = useUISize();

  const goToTerms = () => {
    onClose();
    void safelyOpenURL(preparePolicyUrl(t('link3', { ns: 'privacy' }), i18nLanguage, user));
  };

  const MYSELF = '__myself__';
  const whoIsInsured = !details.profile ? MYSELF : details.profile.firstName;
  const myselfIsInsured = whoIsInsured === MYSELF;

  const scale = 1.5;
  const steps: StepProps[] = [
    { text: t('details.how.step1'), Icon: ChatBubble, size: 20 * scale },
    { text: t('details.how.step2'), Icon: IconClock, size: 20 * scale },
    { text: t('details.how.step3'), Icon: IconCircleCheck, size: 16 * scale },
  ];

  return (
    <BottomSheet
      onClose={onClose}
      open
      showIndicator
      CustomHeaderComponent={
        <Row
          alignItems="center"
          backgroundColor="surface.tertiary-neutral"
          gap="spacing.general.x1"
          paddingHorizontal="spacing.general.x6"
          paddingVertical="spacing.general.x4"
          style={{
            borderTopLeftRadius: theme.borderRadii['radius.xl'],
            borderTopRightRadius: theme.borderRadii['radius.xl'],
            position: 'relative',
            width: '100%',
            justifyContent: 'center',
          }}
        >
          <InsuredBadge color={theme.colors['surface.tertiary']} />
          <Text fontWeight="700" textAlign="center" variant="headerXSmall">
            {t('details.title')}
          </Text>
          <Button
            accessibilityLabel={t('close', { ns: 'accessibility' })}
            hitSlop={20}
            onPress={onClose}
            pressableType={PressableType.GestureHandler}
            variant="plain"
            style={{
              position: 'absolute',
              right: isLargeUI ? theme.spacing['spacing.general.x4'] : theme.spacing['spacing.general.x2'],
              padding: theme.spacing['spacing.general.x1'],
            }}
          >
            <IconX strokeWidth={ICON_STROKE_WIDTH} size={24} color={theme.colors['text.primary']} />
          </Button>
        </Row>
      }
    >
      <Column
        gap="spacing.general.x8"
        paddingBottom="spacing.general.x8"
        paddingHorizontal="spacing.general.x6"
        paddingTop="spacing.general.x4"
      >
        <Text variant="bodyBase">
          <Trans
            components={{ strong: <Text fontWeight="700" /> }}
            i18nKey={myselfIsInsured ? 'details.text.own' : 'details.text.other'}
            ns="insured"
            values={{ name: whoIsInsured }}
          />
        </Text>

        <Column gap="spacing.general.x4">
          <Text variant="bodyBase">{t('details.how.intro')}</Text>
          <Column gap="spacing.general.x3">
            {steps.map(({ text, Icon, size }) => (
              <Row key={text} alignItems="center" gap="spacing.general.x4">
                <Column
                  alignItems="center"
                  justifyContent="center"
                  style={{
                    flexShrink: 0,
                    height: 38,
                    width: 38,
                    backgroundColor: theme.colors['surface.tertiary-neutral'],
                    borderRadius: 19,
                  }}
                >
                  <Icon
                    fillColor={theme.colors['surface.tertiary-neutral']}
                    color={theme.colors['text.brand']}
                    height={size}
                    width={size}
                  />
                </Column>
                <Text flex={1} variant="bodyBase">
                  {text}
                </Text>
              </Row>
            ))}
          </Column>
        </Column>

        <Button
          onPress={goToTerms}
          pressableType={PressableType.GestureHandler}
          style={{
            borderColor: theme.colors['text.brand'],
            borderRadius: theme.borderRadii['radius.xl'],
            borderWidth: 1.5,
            paddingHorizontal: theme.spacing['spacing.general.x4'],
            paddingVertical: theme.spacing['spacing.general.x2'],
          }}
          variant="plain"
        >
          <Text fontWeight="600" style={{ color: theme.colors['text.brand'] }} textAlign="center" variant="bodyBase">
            {t('details.link')}
          </Text>
        </Button>
      </Column>
    </BottomSheet>
  );
}
